import {
  Box,
  Heading,
  Text,
  Card,
  CardBody,
  CardHeader,
  VStack,
  HStack,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Icon,
  Tooltip,
} from '@chakra-ui/react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FiArchive,
  FiClock,
  FiCode,
  FiCpu,
  FiDollarSign,
  FiDroplet,
  FiHardDrive,
  FiKey,
  FiRadio,
  FiShare2,
  FiZap,
} from 'react-icons/fi'
import { SiSolana } from 'react-icons/si'
import { CacheMonitor } from '@/components/monitoring/CacheMonitor'
import { GasPriceMonitor } from '@/components/monitoring/GasPriceMonitor'
import { TronFeeMonitor } from '@/components/monitoring/TronFeeMonitor'
import { RpcMonitor } from '@/components/monitoring/RpcMonitor'
import { PlatformWalletMonitor } from '@/components/monitoring/PlatformWalletMonitor'
import { PlatformContractsMonitor } from '@/components/monitoring/PlatformContractsMonitor'
import { WebhookMonitor } from '@/components/monitoring/WebhookMonitor'
import { SolanaMonitor } from '@/components/monitoring/SolanaMonitor'
import { OrderSideEffectJobsMonitor } from '@/components/monitoring/OrderSideEffectJobsMonitor'
import { QueueQuarantineMonitor } from '@/components/monitoring/QueueQuarantineMonitor'
import { TestnetFaucetMonitor } from '@/components/monitoring/TestnetFaucetMonitor'
import { CooldownRecordsMonitor } from '@/components/monitoring/CooldownRecordsMonitor'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { withNetworkStorageKey } from '@/utils/storageKeys'
import { DEFAULT_MONITORING_TAB, MONITORING_TABS } from './monitoringTabs'
import { useAdminTabQuery } from '@/navigation/adminTabQuery'

export function MonitoringPage() {
  const { t } = useTranslation()
  const { currentEnv } = useNetworkEnvironment()
  const TAB_STORAGE_KEY = useMemo(
    () => withNetworkStorageKey('admin_portal_monitoring_tab_v2', currentEnv),
    [currentEnv]
  )
  const { activeTab, activeTabIndex, handleTabChange } = useAdminTabQuery({
    tabs: MONITORING_TABS,
    defaultTab: DEFAULT_MONITORING_TAB,
    storageKey: TAB_STORAGE_KEY,
  })
  const cardBg = useColorModeValue('white', 'gray.700')
  const cooldownTabHoverBg = useColorModeValue('gray.100', 'whiteAlpha.100')
  const cooldownTabHoverColor = useColorModeValue('admin.600', 'admin.200')
  const cooldownTooltipBg = useColorModeValue('gray.800', 'gray.100')
  const cooldownTooltipColor = useColorModeValue('white', 'gray.800')
  return (
    <Box>
      <VStack align="stretch" spacing={6}>
        <Box>
          <HStack
            justify={{ base: 'flex-start', md: 'space-between' }}
            align={{ base: 'stretch', md: 'start' }}
            mb={2}
            flexWrap="wrap"
            gap={3}
          >
            <Box flex={{ base: '1 1 100%', md: '1 1 auto' }} minW={0}>
              <Heading size="lg" mb={2}>
                {t('monitoring.title')}
              </Heading>
              <Text color="gray.600">{t('monitoring.subtitle')}</Text>
            </Box>
          </HStack>
        </Box>

        <Tabs
          variant="enclosed"
          colorScheme="admin"
          isLazy
          lazyBehavior="keepMounted"
          index={activeTabIndex}
          onChange={handleTabChange}
        >
          <TabList
            overflowX={{ base: 'auto', md: 'visible' }}
            overflowY="hidden"
          >
            <Tab
              aria-label={t('monitoring.tabs.gasFee')}
              minW="56px"
              justifyContent="center"
            >
              <Tooltip label={t('monitoring.tabs.gasFee')} hasArrow>
                <Box display="flex" alignItems="center">
                  <Icon as={FiDollarSign} boxSize={6} />
                </Box>
              </Tooltip>
            </Tab>
            <Tab
              aria-label={t('monitoring.tabs.rpc')}
              minW="56px"
              justifyContent="center"
            >
              <Tooltip label={t('monitoring.tabs.rpc')} hasArrow>
                <Box display="flex" alignItems="center">
                  <Icon as={FiRadio} boxSize={6} />
                </Box>
              </Tooltip>
            </Tab>
            <Tab
              aria-label={t('monitoring.tabs.platformWallet')}
              minW="56px"
              justifyContent="center"
            >
              <Tooltip label={t('monitoring.tabs.platformWallet')} hasArrow>
                <Box display="flex" alignItems="center">
                  <Icon as={FiKey} boxSize={6} />
                </Box>
              </Tooltip>
            </Tab>
            <Tab
              aria-label={t('monitoring.tabs.cache')}
              minW="56px"
              justifyContent="center"
            >
              <Tooltip label={t('monitoring.tabs.cache')} hasArrow>
                <Box display="flex" alignItems="center">
                  <Icon as={FiHardDrive} boxSize={6} />
                </Box>
              </Tooltip>
            </Tab>
            <Tab
              aria-label={t('monitoring.tabs.contracts')}
              minW="56px"
              justifyContent="center"
            >
              <Tooltip label={t('monitoring.tabs.contracts')} hasArrow>
                <Box display="flex" alignItems="center">
                  <Icon as={FiCode} boxSize={6} />
                </Box>
              </Tooltip>
            </Tab>
            <Tab
              aria-label={t('monitoring.tabs.webhook')}
              minW="56px"
              justifyContent="center"
            >
              <Tooltip label={t('monitoring.tabs.webhook')} hasArrow>
                <Box display="flex" alignItems="center">
                  <Icon as={FiShare2} boxSize={6} />
                </Box>
              </Tooltip>
            </Tab>
            <Tab
              aria-label={t('monitoring.tabs.solana')}
              minW="56px"
              justifyContent="center"
            >
              <Tooltip label={t('monitoring.tabs.solana')} hasArrow>
                <Box display="flex" alignItems="center">
                  <Icon as={SiSolana} boxSize={6} />
                </Box>
              </Tooltip>
            </Tab>
            <Tab
              aria-label={t('monitoring.tabs.sideEffectJobs')}
              minW="56px"
              justifyContent="center"
            >
              <Tooltip label={t('monitoring.tabs.sideEffectJobs')} hasArrow>
                <Box display="flex" alignItems="center">
                  <Icon as={FiCpu} boxSize={6} />
                </Box>
              </Tooltip>
            </Tab>
            <Tab
              aria-label={t('monitoring.tabs.queueQuarantine')}
              minW="56px"
              justifyContent="center"
            >
              <Tooltip label={t('monitoring.tabs.queueQuarantine')} hasArrow>
                <Box display="flex" alignItems="center">
                  <Icon as={FiArchive} boxSize={6} />
                </Box>
              </Tooltip>
            </Tab>
            <Tab
              aria-label={t('monitoring.tabs.testnetFaucet')}
              minW="56px"
              justifyContent="center"
            >
              <Tooltip label={t('monitoring.tabs.testnetFaucet')} hasArrow>
                <Box display="flex" alignItems="center">
                  <Icon as={FiDroplet} boxSize={6} />
                </Box>
              </Tooltip>
            </Tab>
            <Tab
              aria-label={t('monitoring.tabs.cooldown')}
              minW="56px"
              justifyContent="center"
              transition="background-color 0.15s ease, color 0.15s ease"
              _hover={{ bg: cooldownTabHoverBg, color: cooldownTabHoverColor }}
              _focusVisible={{
                boxShadow: 'outline',
                color: cooldownTabHoverColor,
              }}
            >
              <Tooltip
                label={t('monitoring.tabs.cooldown')}
                placement="top"
                openDelay={300}
                closeDelay={100}
                gutter={10}
                hasArrow
                bg={cooldownTooltipBg}
                color={cooldownTooltipColor}
                borderRadius="md"
                px={3}
                py={2}
                fontSize="sm"
                fontWeight="medium"
                boxShadow="lg"
              >
                <Box display="flex" alignItems="center">
                  <Icon as={FiClock} boxSize={6} />
                </Box>
              </Tooltip>
            </Tab>
          </TabList>

          <TabPanels>
            {/* Gas Fee Monitor Tab */}
            <TabPanel p={0} pt={4}>
              <VStack spacing={4} align="stretch">
                <TronFeeMonitor key={`tron-fee-${currentEnv}`} />
                {/* Gas Price Monitor */}
                <Card bg={cardBg}>
                  <CardHeader pb={2}>
                    <HStack>
                      <FiZap />
                      <Heading size="md">
                        {t('monitoring.labels.gasPriceMonitor')}
                      </Heading>
                    </HStack>
                  </CardHeader>
                  <CardBody pt={1}>
                    <GasPriceMonitor key={`gas-price-${currentEnv}`} />
                  </CardBody>
                </Card>
              </VStack>
            </TabPanel>

            {/* RPC Tab */}
            <TabPanel p={0} pt={4}>
              <RpcMonitor key={`rpc-${currentEnv}`} />
            </TabPanel>

            {/* Platform Wallet Tab */}
            <TabPanel p={0} pt={4}>
              <PlatformWalletMonitor key={`platform-wallet-${currentEnv}`} />
            </TabPanel>

            {/* Cache Tools Tab */}
            <TabPanel p={0} pt={4}>
              <CacheMonitor key={`cache-tools-${currentEnv}`} />
            </TabPanel>

            {/* Contracts Tab */}
            <TabPanel p={0} pt={4}>
              <PlatformContractsMonitor
                key={`platform-contracts-${currentEnv}`}
              />
            </TabPanel>

            {/* Webhook Tab */}
            <TabPanel p={0} pt={4}>
              <WebhookMonitor key={`webhook-monitor-${currentEnv}`} />
            </TabPanel>

            {/* Solana Tab */}
            <TabPanel p={0} pt={4}>
              <SolanaMonitor
                key={`solana-monitor-${currentEnv}`}
                isActive={activeTab === 'solana'}
              />
            </TabPanel>

            {/* Side Effect Jobs Tab */}
            <TabPanel p={0} pt={4}>
              <OrderSideEffectJobsMonitor
                key={`side-effect-jobs-${currentEnv}`}
                isActive={activeTab === 'side_effect_jobs'}
              />
            </TabPanel>

            {/* Queue Quarantine Tab */}
            <TabPanel p={0} pt={4}>
              <QueueQuarantineMonitor
                key={`queue-quarantine-${currentEnv}`}
                isActive={activeTab === 'queue_quarantine'}
              />
            </TabPanel>

            {/* Testnet Faucet Tab */}
            <TabPanel p={0} pt={4}>
              <TestnetFaucetMonitor
                key={`testnet-faucet-${currentEnv}`}
                isActive={activeTab === 'testnet_faucet'}
              />
            </TabPanel>

            {/* Cooldown Records Tab */}
            <TabPanel p={0} pt={4}>
              <CooldownRecordsMonitor
                key={`cooldown-records-${currentEnv}`}
                isActive={activeTab === 'cooldown'}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Box>
  )
}

export default MonitoringPage
